import {
  AsyncSelectFetchParams,
  AsyncSelectFetchResult,
  AutocompleteFetchFunction,
} from "@/components/form/types";
import { axiosInstance } from "@/lib/axiosInstance";

export const fetchRoleOptions = async ({
  search,
  page,
  pageSize,
}: AsyncSelectFetchParams): Promise<AsyncSelectFetchResult> => {
  const res = await axiosInstance.get("/v1/roles", {
    params: {
      active_only: true,
      search,
      page,
      limit: pageSize,
    },
  });

  const roles = res.data?.data || res.data?.items || res.data || [];
  const total = res.data?.total || roles.length;
  const hasMore = res.data?.has_more || false;

  return {
    items: roles.map((role: any) => ({
      label: role.name,
      value: role.id,
    })),
    hasMore,
    total,
  };
};

export type RoleCreateType = {
  name: string;
  description?: string;
  designation_id: string;
  permission_ids: string[];
};

export type PermissionType = {
  id: string;
  name: string;
  description: string;
};

export type RoleTemplate = {
  id: string;
  name: string;
  description: string;
  designation: string;
  permissions: string[]; // permission name strings e.g. "department.read"
};

export const fetchRoleTemplates = async (): Promise<RoleTemplate[]> => {
  const { data } = await axiosInstance.get("/v1/roles/templates");
  return data?.data || data || [];
};

export type RoleUpdateType = RoleCreateType; // same shape as create

export type RoleType = {
  id: string;
  college_id: string;
  name: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export const createRole = async (payload: RoleCreateType) => {
  const { data } = await axiosInstance.post("/v1/roles", payload);
  return data;
};

export const updateRole = async (roleId: string, payload: RoleUpdateType) => {
  const { data } = await axiosInstance.put(`/v1/roles/${roleId}`, payload);
  return data;
};

export const fetchRoleById = async (roleId: string): Promise<RoleType> => {
  const { data } = await axiosInstance.get(`/v1/roles/${roleId}`);
  return data?.data || data;
};

export const fetchRolesByDesignation = async (designationId?: string): Promise<RoleType[]> => {
  const { data } = await axiosInstance.get("/v1/roles", {
    params: { active_only: true, ...(designationId ? { for_designation_id: designationId } : {}) },
  });
  return data?.data || data?.items || data || [];
};

export const fetchRolePermissions = async (roleId?: string): Promise<PermissionType[]> => {
  const url = roleId ? `/v1/roles/${roleId}/permissions` : "/v1/roles/permissions";
  const { data } = await axiosInstance.get(url);
  return data?.data || data || [];
};

export const updateRolePermissions = async (roleId: string, payload: { permission_ids: string[] }) => {
  const { data } = await axiosInstance.put(`/v1/roles/${roleId}/permissions`, payload);
  return data;
};

export const fetchAutocompleteRoleOptions: AutocompleteFetchFunction = async ({
  search,
  limit,
}) => {
  const res = await axiosInstance.get("/v1/roles", {
    params: {
      active_only: true,
      search,
      limit,
    },
  });

  const roles = res.data?.data || res.data?.items || res.data || [];

  return roles.map((role: any) => ({
    label: role.name,
    value: role.id,
  }));
};

export const roleService = {
  fetchRoleOptions,
  fetchAutocompleteRoleOptions,
  createRole,
  updateRole,
  fetchRoleById,
  fetchRolesByDesignation,
  fetchRolePermissions,
  updateRolePermissions,
  fetchRoleTemplates,
};
